/**
 * Dashboard - The Monarch San Antonio — Digital Marketing Intelligence
 *
 * Data source: GA4 Property 484092824 (Feb 26 – Mar 25, 2026)
 * Account: analytics@glueiq.agency
 *
 * Story: "Paid Social Fills the Lobby, But Who's Actually Booking?"
 *
 * Key findings:
 *  - Paid Social = 33% of sessions but worst engagement (60.5%, 3s avg time)
 *  - Organic Search = quality champion (91.7% engagement, 44s avg time)
 *  - Paid Search = highest conversion intent (0.14% key event rate)
 *  - 13K reservation button clicks → 118 form submissions = 0.9% conversion
 *  - Dining pages dominate content interest (49K+ views)
 *  - Retention cliff: only 3.4% return after week 1
 */

import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, fadeInUp, pageTransition, pageInitial, pageAnimate } from '@/lib/motionVariants';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { ChevronRight } from 'lucide-react';
import type { DashboardSection } from '@/types/dashboard';
import useMonarchStore from '@/stores/monarchStore';
import { useChatStore } from '@/stores/chatStore';
import { formatNumber, formatPercentRaw, formatDuration } from '@/lib/formatting';
import { TEXT, BORDER, BRAND, CHART_COLORS, getChartTooltip, cssVar } from '../charts/utils';
import { useThemeKey } from '@/hooks/useThemeKey';
import ChannelDrillPanel from '../drill/ChannelDrillPanel';
import type { ChannelData } from '../drill/ChannelDrillPanel';
import DeltaIndicator from '../shared/DeltaIndicator';
import PageHeader from '../shared/PageHeader';
import { KPI_COMPARISON, CHANNEL_COMPARISON } from '@/data/comparisonData';
import { useComparisonPeriods } from '@/hooks/useComparisonPeriods';
import { useOverview, useChannels, usePages } from '@/hooks/useGA4Data';

/* ============================================================================
   Chat Drill-Down Helper
   ============================================================================ */

const askChat = (query: string, e?: React.MouseEvent) => {
  if (e) {
    useChatStore.getState().showAnalyzePrompt(
      query,
      '',
      (e.currentTarget as HTMLElement).getBoundingClientRect(),
    );
  } else {
    useChatStore.getState().sendAnalyzeQuery(query);
  }
};

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

/* ============================================================================
   Real GA4 Data — Property 484092824 — Feb 26 – Mar 25, 2026
   ============================================================================ */

const GA4_KPIS = {
  sessions: 87_615,
  activeUsers: 71_000,
  newUsers: 71_000,
  pageviews: 266_000,       // page_view events
  avgEngagementTime: 23,     // seconds per session
  engagementRate: 79.49,
  eventCount: 557_310,
  keyEvents: 118,            // homepage_form_submission
  reservationClicks: 13_000, // navigation_reservation_button_click
  realtimeUsers: 41,
};

const GA4_CHANNELS = [
  { channel: 'Paid Social',    sessions: 29_131, engagedSessions: 17_631, engagementRate: 60.52, avgTime: 3,  eventsPerSession: 3.94, eventCount: 114_846, keyEvents: 51, keyEventRate: 0.11 },
  { channel: 'Organic Search', sessions: 22_137, engagedSessions: 20_304, engagementRate: 91.72, avgTime: 44, eventsPerSession: 8.38, eventCount: 185_493, keyEvents: 16, keyEventRate: 0.05 },
  { channel: 'Direct',         sessions: 15_236, engagedSessions: 13_720, engagementRate: 90.05, avgTime: 21, eventsPerSession: 6.49, eventCount: 98_814,  keyEvents: 17, keyEventRate: 0.07 },
  { channel: 'Paid Search',    sessions: 11_654, engagedSessions: 10_891, engagementRate: 93.45, avgTime: 40, eventsPerSession: 8.13, eventCount: 94_743,  keyEvents: 24, keyEventRate: 0.14 },
  { channel: 'Organic Social', sessions: 4_340,  engagedSessions: 4_259,  engagementRate: 98.13, avgTime: 22, eventsPerSession: 8.16, eventCount: 35_413,  keyEvents: 3,  keyEventRate: 0.05 },
  { channel: 'Paid Other',     sessions: 3_630,  engagedSessions: 3_266,  engagementRate: 89.97, avgTime: 7,  eventsPerSession: 4.43, eventCount: 16_075,  keyEvents: 2,  keyEventRate: 0.03 },
  { channel: 'Referral',       sessions: 1_223,  engagedSessions: 1_125,  engagementRate: 91.99, avgTime: 50, eventsPerSession: 7.69, eventCount: 9_404,   keyEvents: 5,  keyEventRate: 0.16 },
  { channel: 'Cross-network',  sessions: 197,    engagedSessions: 184,    engagementRate: 93.40, avgTime: 45, eventsPerSession: 8.54, eventCount: 1_683,   keyEvents: 0,  keyEventRate: 0 },
  { channel: 'Email',          sessions: 42,     engagedSessions: 40,     engagementRate: 95.24, avgTime: 26, eventsPerSession: 6.62, eventCount: 278,     keyEvents: 0,  keyEventRate: 0 },
];

const TOP_PAGES = [
  { page: 'Homepage',      views: 133_000 },
  { page: 'Dining',        views: 49_000 },
  { page: 'Aleteo',        views: 27_000 },
  { page: 'Oak & Amber',   views: 19_000 },
  { page: 'Amenities',     views: 8_500 },
  { page: 'Accommodations', views: 7_000 },
  { page: 'Nectarie Cafe', views: 4_900 },
];

const RETENTION_WEEKS = [
  { week: 'Week 0', rate: 100 },
  { week: 'Week 1', rate: 3.4 },
  { week: 'Week 2', rate: 1.7 },
  { week: 'Week 3', rate: 1.1 },
  { week: 'Week 4', rate: 0.8 },
  { week: 'Week 5', rate: 0.6 },
];

const CONVERSION_FUNNEL = [
  { label: 'Total Sessions',              value: 87_615, color: BRAND.gold },
  { label: 'Engaged Sessions',            value: 69_649, color: CHART_COLORS[2] },
  { label: 'Reservation Button Clicks',   value: 13_000, color: CHART_COLORS[3] },
  { label: 'Form Submissions',            value: 118,    color: CHART_COLORS[5] },
];

/* ============================================================================
   KPI Card
   ============================================================================ */

interface KPICardProps {
  label: string;
  value: string;
  subtitle?: string;
  accent?: boolean;
  alert?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  delta?: { current: number; yoyPrior: number; popPrior: number; invertColor?: boolean; popTooltip?: string; yoyTooltip?: string };
}

const KPICard: React.FC<KPICardProps> = ({ label, value, subtitle, accent, alert, onClick, delta }) => (
  <div
    className={`bg-bg-1 border rounded-xl p-4 min-w-0 overflow-hidden transition-colors ${
      alert ? 'border-danger/40' : accent ? 'border-monarch-gold/40' : 'border-border'
    } ${onClick ? 'cursor-pointer hover:border-monarch-gold/60 hover:bg-bg-2' : ''}`}
    onClick={onClick}
  >
    <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1.5 truncate">{label}</p>
    <p className={`font-mono text-lg font-bold truncate ${
      alert ? 'text-danger' : accent ? 'text-monarch-gold' : 'text-text'
    }`}>{value}</p>
    {delta && (delta.popPrior > 0 || delta.yoyPrior > 0) && (
      <div className="mt-1">
        <DeltaIndicator current={delta.current} yoyPrior={delta.yoyPrior} popPrior={delta.popPrior} invertColor={delta.invertColor} popTooltip={delta.popTooltip} yoyTooltip={delta.yoyTooltip} />
      </div>
    )}
    {subtitle && (
      <p className="text-xs text-text-3 mt-0.5 truncate">{subtitle}</p>
    )}
  </div>
);

/* ============================================================================
   Insight Card — AI-style callout
   ============================================================================ */

interface InsightCardProps {
  icon: string;
  title: string;
  body: string;
  severity: 'warning' | 'critical' | 'positive' | 'neutral';
  onClick?: (e: React.MouseEvent) => void;
}

const SEVERITY_COLORS = {
  warning: { border: 'border-warning/30', bg: 'bg-warning/5', icon: 'text-warning' },
  critical: { border: 'border-danger/30', bg: 'bg-danger/5', icon: 'text-danger' },
  positive: { border: 'border-success/30', bg: 'bg-success/5', icon: 'text-success' },
  neutral: { border: 'border-monarch-gold/30', bg: 'bg-monarch-gold/5', icon: 'text-monarch-gold' },
};

const InsightCard: React.FC<InsightCardProps> = ({ icon, title, body, severity, onClick }) => {
  const c = SEVERITY_COLORS[severity];
  return (
    <div
      className={`${c.bg} ${c.border} border rounded-xl p-4 ${onClick ? 'cursor-pointer hover:brightness-110 transition-all' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <span className={`text-lg ${c.icon} flex-shrink-0 mt-0.5`}>{icon}</span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-text mb-1">{title}</p>
          <p className="text-xs text-text-3 leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
   Booking Funnel
   ============================================================================ */

interface FunnelStage {
  label: string;
  value: number;
  color: string;
}

const BookingFunnel: React.FC<{ stages: FunnelStage[] }> = ({ stages }) => {
  const maxValue = stages[0]?.value || 1;

  return (
    <div className="space-y-3">
      {stages.map((stage, idx) => {
        const widthPct = Math.max((stage.value / maxValue) * 100, 4);
        const dropOff = idx > 0 && stages[idx - 1].value > 0
          ? ((stages[idx - 1].value - stage.value) / stages[idx - 1].value * 100)
          : null;

        return (
          <div key={stage.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-text-2">{stage.label}</span>
              <span className="text-xs font-mono font-semibold text-text">
                {formatNumber(stage.value)}
              </span>
            </div>
            <div className="relative h-7 bg-bg-2 rounded-lg overflow-hidden">
              <div
                className="h-full rounded-lg transition-all duration-500"
                style={{ width: `${widthPct}%`, backgroundColor: stage.color }}
              />
            </div>
            {dropOff != null && dropOff > 0 && (
              <p className={`text-xs mt-0.5 text-right font-mono ${
                dropOff > 80 ? 'text-danger' : 'text-text-3'
              }`}>
                -{dropOff.toFixed(1)}% drop-off
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ============================================================================
   Channel Quality Indicator — dot with engagement rate
   ============================================================================ */

const EngagementDot: React.FC<{ rate: number }> = ({ rate }) => {
  const colorClass = rate >= 90 ? 'bg-success' : rate >= 75 ? 'bg-warning' : 'bg-danger';
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${colorClass}`} />
      <span className="font-mono">{formatPercentRaw(rate)}</span>
    </span>
  );
};

/* ============================================================================
   Dashboard Page
   ============================================================================ */

export const Dashboard: React.FC = () => {
  const setActiveSection = useMonarchStore((s) => s.setActiveSection);
  const { compareEnabled, popTooltip, yoyTooltip } = useComparisonPeriods();
  const navigateTo = useCallback(
    (section: DashboardSection) => setActiveSection(section),
    [setActiveSection],
  );

  // Live API data with hardcoded fallbacks
  const { data: overview } = useOverview();
  const { data: liveChannels } = useChannels();
  const { data: livePages } = usePages();

  const kpis = {
    sessions: overview?.sessions ?? GA4_KPIS.sessions,
    activeUsers: overview?.activeUsers ?? GA4_KPIS.activeUsers,
    newUsers: overview?.newUsers ?? GA4_KPIS.newUsers,
    pageviews: overview?.pageviews ?? GA4_KPIS.pageviews,
    avgEngagementTime: overview?.avgEngagementTime ?? GA4_KPIS.avgEngagementTime,
    engagementRate: overview?.engagementRate ?? GA4_KPIS.engagementRate,
    eventCount: overview?.eventCount ?? GA4_KPIS.eventCount,
    reservationClicks: overview?.conversions ?? GA4_KPIS.reservationClicks,
    keyEvents: GA4_KPIS.keyEvents, // form submissions not in overview endpoint
    realtimeUsers: GA4_KPIS.realtimeUsers, // stays hardcoded until realtime hook
  };

  const channels = liveChannels ?? GA4_CHANNELS;
  const pages = livePages?.map(p => ({ page: p.page, views: p.views })) ?? TOP_PAGES;

  const kpiDelta = (key: keyof typeof KPI_COMPARISON) =>
    compareEnabled ? { current: KPI_COMPARISON[key].current, yoyPrior: KPI_COMPARISON[key].yoy, popPrior: KPI_COMPARISON[key].pop, popTooltip, yoyTooltip } : undefined;

  // Channel drill panel state
  const [selectedChannel, setSelectedChannel] = useState<ChannelData | null>(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const handleChannelClick = useCallback((channel: typeof GA4_CHANNELS[number]) => {
    setSelectedChannel(channel);
    setDrillOpen(true);
  }, []);
  const handleDrillClose = useCallback(() => {
    setDrillOpen(false);
    setSelectedChannel(null);
  }, []);

  // Top 7 channels for charts (exclude tiny ones)
  const topChannels = channels.slice(0, 7);

  // Channel session mix — Doughnut
  const channelMixData = useMemo(() => ({
    labels: topChannels.map((c) => c.channel),
    datasets: [{
      data: topChannels.map((c) => c.sessions),
      backgroundColor: [
        CHART_COLORS[5], // Paid Social — red to signal issue
        CHART_COLORS[2], // Organic Search — blue
        BRAND.gold,      // Direct — gold
        CHART_COLORS[1], // Paid Search — emerald
        CHART_COLORS[4], // Organic Social — purple
        CHART_COLORS[3], // Paid Other — amber
        CHART_COLORS[6], // Referral — cyan
      ],
      borderWidth: 0,
      hoverOffset: 4,
    }],
  }), [topChannels]);

  // Engagement rate bar chart — highlights Paid Social problem
  const engagementBarData = useMemo(() => ({
    labels: topChannels.map((c) => c.channel),
    datasets: [{
      label: 'Engagement Rate',
      data: topChannels.map((c) => c.engagementRate),
      backgroundColor: topChannels.map((c) =>
        c.engagementRate >= 90 ? cssVar('--color-success', '#10b981') :
        c.engagementRate >= 75 ? cssVar('--color-warning', '#f59e0b') : cssVar('--color-danger', '#ef4444')
      ),
      borderRadius: 4,
    }],
  }), [topChannels]);

  // Channel sessions + key events bar chart
  const channelBarData = useMemo(() => ({
    labels: topChannels.map((c) => c.channel),
    datasets: [
      {
        label: 'Sessions',
        data: topChannels.map((c) => c.sessions),
        backgroundColor: BRAND.gold,
        borderRadius: 4,
      },
      {
        label: 'Engaged Sessions',
        data: topChannels.map((c) => c.engagedSessions),
        backgroundColor: cssVar('--color-success', '#10b981'),
        borderRadius: 4,
      },
    ],
  }), [topChannels]);

  // Top pages horizontal bar
  const topPagesData = useMemo(() => ({
    labels: pages.map((p) => p.page),
    datasets: [{
      label: 'Page Views',
      data: pages.map((p) => p.views),
      backgroundColor: pages.map((_, i) => i === 0 ? BRAND.gold : CHART_COLORS[2]),
      borderRadius: 4,
    }],
  }), [pages]);

  // Retention line chart
  const retentionData = useMemo(() => ({
    labels: RETENTION_WEEKS.map((r) => r.week),
    datasets: [{
      label: 'Returning Users %',
      data: RETENTION_WEEKS.map((r) => r.rate),
      borderColor: cssVar('--color-danger', '#ef4444'),
      backgroundColor: 'rgba(239, 68, 68, 0.08)',
      borderWidth: 2,
      pointRadius: 4,
      pointBackgroundColor: cssVar('--color-danger', '#ef4444'),
      tension: 0.3,
      fill: true,
    }],
  }), []);

  const themeKey = useThemeKey();
  const chartTooltip = getChartTooltip();

  return (
    <motion.div className="space-y-6" initial={pageInitial} animate={pageAnimate} transition={pageTransition}>
      <PageHeader
        title="Digital Marketing Intelligence"
        subtitle="GA4 Property 484092824 | 28-day window"
        badges={
          <>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-monarch-gold/10 text-monarch-gold border border-monarch-gold/20">
              <span className="w-2 h-2 rounded-full bg-current" />
              THE MONARCH SA
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-info/10 text-info border border-info/20">
              <span className="w-2 h-2 rounded-full bg-current" />
              GA4 LIVE DATA
            </span>
          </>
        }
      />

      {/* 1. HERO KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <KPICard label="Sessions" value={formatNumber(kpis.sessions)} subtitle="28-day total" delta={kpiDelta('sessions')} />
        <KPICard label="Active Users" value={formatNumber(kpis.activeUsers)} subtitle={`${formatNumber(kpis.newUsers)} new users`} delta={kpiDelta('activeUsers')} />
        <KPICard label="Pageviews" value={formatNumber(kpis.pageviews)} subtitle="page_view events" delta={kpiDelta('pageviews')} />
        <KPICard label="Engagement Time" value={formatDuration(kpis.avgEngagementTime)} subtitle="avg per session" delta={kpiDelta('avgEngagementTime')} />
        <KPICard label="Engagement Rate" value={formatPercentRaw(kpis.engagementRate)} subtitle="engaged / total" accent delta={kpiDelta('engagementRate')} />
        <KPICard label="Rsv. Btn Clicks" value={formatNumber(kpis.reservationClicks)} subtitle="nav button clicks" accent delta={kpiDelta('reservationClicks')} />
        <KPICard label="Form Submissions" value={kpis.keyEvents.toString()} subtitle="0.13% of sessions" alert onClick={(e) => askChat('Why do we only have 118 form submissions from 87.6K sessions? What is the bottleneck?', e)} delta={kpiDelta('keyEvents')} />
      </div>

      {/* 2. KEY INSIGHTS — Story cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <InsightCard
          icon="!!"
          title="Paid Social: Volume vs Quality"
          body="33% of all sessions but only 60.5% engagement rate (3s avg time). Highest volume channel is burning budget on low-quality traffic."
          severity="critical"
          onClick={(e) => askChat('Paid Social has 33% of sessions but only 60.5% engagement. Analyze the quality gap and recommend budget reallocation.', e)}
        />
        <InsightCard
          icon=">>"
          title="Massive Funnel Drop-Off"
          body="13,000 reservation button clicks but only 118 form submissions (0.9% conversion). Something is blocking the booking flow."
          severity="critical"
          onClick={(e) => askChat('13K reservation clicks but only 118 form submissions — a 99.1% drop-off. What could be causing this?', e)}
        />
        <InsightCard
          icon="**"
          title="Organic Search = Quality King"
          body="91.7% engagement rate, 44s avg time, 8.38 events/session. Lowest cost, highest quality traffic source."
          severity="positive"
          onClick={(e) => askChat('Organic Search has 91.7% engagement and 44s avg time. How can we scale this channel?', e)}
        />
        <InsightCard
          icon="++"
          title="Paid Search = Best Intent"
          body="93.5% engagement, 0.14% key event rate — highest conversion rate of any channel. Scale this first."
          severity="positive"
          onClick={(e) => askChat('Paid Search converts at 0.14% key event rate — the best of any channel. What budget increase would optimize ROI?', e)}
        />
      </div>

      {/* 3. Session Mix + Booking Conversion Funnel (side by side) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Session Mix */}
        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm font-normal text-text-2 mb-4">Session Mix by Channel</h3>
          <div className="flex items-center justify-center" style={{ height: 260 }}>
            <Doughnut
              key={`channel-mix-${themeKey}`}
              data={channelMixData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: '55%',
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                    labels: {
                      color: TEXT.muted,
                      padding: 12,
                      usePointStyle: true,
                      pointStyle: 'circle',
                      font: { size: 10 },
                    },
                  },
                  tooltip: chartTooltip,
                },
                animation: { duration: 600, easing: 'easeOutCubic' },
              }}
            />
          </div>
        </div>

        {/* Booking Conversion Funnel */}
        <div className="lg:col-span-2 bg-bg-1 border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-sm font-normal text-text-2">Booking Conversion Funnel</h3>
            <button
              onClick={() => navigateTo('conversions')}
              className="text-xs font-medium text-monarch-gold hover:text-monarch-gold-light transition-colors cursor-pointer flex items-center gap-1"
            >
              Details
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </button>
          </div>
          <BookingFunnel stages={CONVERSION_FUNNEL} />
          <div className="mt-4 p-3 rounded-lg bg-danger/5 border border-danger/20">
            <p className="text-xs text-danger font-medium">
              99.1% drop from reservation clicks to form submissions
            </p>
            <p className="text-xs text-text-3 mt-1">
              13K users click the reservation button but only 118 complete the form. Investigate the booking widget experience.
            </p>
          </div>
        </div>
      </div>

      {/* 4. Engagement Rate by Channel + Top Pages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engagement Rate by Channel */}
        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-sm font-normal text-text-2">Engagement Rate by Channel</h3>
              <p className="text-xs text-text-3 mt-1">
                <span className="inline-block w-2 h-2 rounded-full bg-success mr-1" />90%+
                <span className="inline-block w-2 h-2 rounded-full bg-warning mx-1 ml-3" />75-89%
                <span className="inline-block w-2 h-2 rounded-full bg-danger mx-1 ml-3" />Below 75%
              </p>
            </div>
            <button
              onClick={() => navigateTo('acquisition')}
              className="text-xs font-medium text-monarch-gold hover:text-monarch-gold-light transition-colors cursor-pointer flex items-center gap-1"
            >
              Acquisition Details
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </button>
          </div>
          <div style={{ height: 260 }}>
            <Bar
              key={`engagement-rate-${themeKey}`}
              data={engagementBarData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y' as const,
                scales: {
                  x: {
                    min: 0,
                    max: 100,
                    grid: { color: BORDER.default },
                    ticks: {
                      color: TEXT.faint,
                      font: { size: 10, family: "'JetBrains Mono', monospace" },
                      callback: (v) => `${v}%`,
                    },
                    border: { display: false },
                  },
                  y: {
                    grid: { display: false },
                    ticks: { color: TEXT.muted, font: { size: 11 } },
                    border: { display: false },
                  },
                },
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    ...chartTooltip,
                    callbacks: {
                      label: (ctx) => `${(ctx.parsed.x ?? 0).toFixed(1)}% engagement rate`,
                    },
                  },
                },
                animation: { duration: 600, easing: 'easeOutCubic' },
              }}
            />
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-sm font-normal text-text-2">Top Pages by Views</h3>
            <button
              onClick={() => navigateTo('engagement')}
              className="text-xs font-medium text-monarch-gold hover:text-monarch-gold-light transition-colors cursor-pointer flex items-center gap-1"
            >
              All Pages
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </button>
          </div>
          <div style={{ height: 260 }}>
            <Bar
              key={`top-pages-${themeKey}`}
              data={topPagesData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y' as const,
                scales: {
                  x: {
                    grid: { color: BORDER.default },
                    ticks: {
                      color: TEXT.faint,
                      font: { size: 10, family: "'JetBrains Mono', monospace" },
                      callback: (v) => formatNumber(Number(v)),
                    },
                    border: { display: false },
                  },
                  y: {
                    grid: { display: false },
                    ticks: { color: TEXT.muted, font: { size: 11 } },
                    border: { display: false },
                  },
                },
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    ...chartTooltip,
                    callbacks: {
                      label: (ctx) => `${formatNumber(ctx.parsed.x ?? 0)} views`,
                    },
                  },
                },
                animation: { duration: 600, easing: 'easeOutCubic' },
              }}
            />
          </div>
          <div className="mt-3 p-3 rounded-lg bg-monarch-gold/5 border border-monarch-gold/20">
            <p className="text-xs text-monarch-gold font-medium">
              Dining content drives 37% of non-homepage views
            </p>
            <p className="text-xs text-text-3 mt-1">
              Aleteo (27K), Oak & Amber (19K), and Nectarie Cafe (4.9K) dominate. Dining is the gateway content.
            </p>
          </div>
        </div>
      </div>

      {/* 5. Channel Sessions + Engaged Sessions */}
      <div className="bg-bg-1 border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-sm font-normal text-text-2">Sessions vs Engaged Sessions by Channel</h3>
          <button
            onClick={() => navigateTo('acquisition')}
            className="text-xs font-medium text-monarch-gold hover:text-monarch-gold-light transition-colors cursor-pointer flex items-center gap-1"
          >
            View All
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </button>
        </div>
        <div style={{ height: 280 }}>
          <Bar
            key={`channel-sessions-${themeKey}`}
            data={channelBarData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              indexAxis: 'y' as const,
              scales: {
                x: {
                  grid: { color: BORDER.default },
                  ticks: {
                    color: TEXT.faint,
                    font: { size: 10, family: "'JetBrains Mono', monospace" },
                    callback: (v) => formatNumber(Number(v)),
                  },
                  border: { display: false },
                },
                y: {
                  grid: { display: false },
                  ticks: { color: TEXT.muted, font: { size: 11 } },
                  border: { display: false },
                },
              },
              plugins: {
                legend: {
                  display: true,
                  position: 'top' as const,
                  align: 'end' as const,
                  labels: { color: TEXT.muted, usePointStyle: true, pointStyle: 'circle', padding: 16, font: { size: 11 } },
                },
                tooltip: chartTooltip,
              },
              animation: { duration: 600, easing: 'easeOutCubic' },
            }}
          />
        </div>
      </div>

      {/* 6. Retention + Channel Data Table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Retention */}
        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-sm font-normal text-text-2">User Retention</h3>
            <button
              onClick={() => navigateTo('audience')}
              className="text-xs font-medium text-monarch-gold hover:text-monarch-gold-light transition-colors cursor-pointer flex items-center gap-1"
            >
              Audience
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </button>
          </div>
          <div style={{ height: 200 }}>
            <Line
              key={`retention-${themeKey}`}
              data={retentionData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: {
                    grid: { display: false },
                    ticks: { color: TEXT.faint, font: { size: 10 } },
                    border: { display: false },
                  },
                  y: {
                    min: 0,
                    max: 100,
                    grid: { color: BORDER.default },
                    ticks: {
                      color: TEXT.faint,
                      font: { size: 10, family: "'JetBrains Mono', monospace" },
                      callback: (v) => `${v}%`,
                    },
                    border: { display: false },
                  },
                },
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    ...chartTooltip,
                    callbacks: {
                      label: (ctx) => `${ctx.parsed.y}% returning`,
                    },
                  },
                },
                animation: { duration: 600, easing: 'easeOutCubic' },
              }}
            />
          </div>
          <div className="mt-3 p-3 rounded-lg bg-danger/5 border border-danger/20">
            <p className="text-xs text-danger font-medium">
              96.6% user drop-off after first week
            </p>
            <p className="text-xs text-text-3 mt-1">
              Nearly all traffic is one-and-done. Retargeting and email capture could dramatically improve this.
            </p>
          </div>
        </div>

        {/* Full Channel Table */}
        <div className="lg:col-span-2 bg-bg-1 border border-border rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-display text-sm font-normal text-text-2">Channel Performance Detail</h3>
            <button
              onClick={() => navigateTo('acquisition')}
              className="text-xs font-medium text-monarch-gold hover:text-monarch-gold-light transition-colors cursor-pointer"
            >
              Full Report
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-bg-2 text-text-3">
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Channel</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Sessions</th>
                  {compareEnabled && <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Change</th>}
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Engaged</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Eng. Rate</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Avg Time</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Events/Sess</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Key Events</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Key Rate</th>
                  <th className="px-4 py-3 w-8" />
                </tr>
              </thead>
              <tbody>
                {channels.map((row, idx) => (
                  <tr
                    key={`${row.channel}-${idx}`}
                    className={`group border-b border-border/50 hover:bg-bg-2 transition-colors cursor-pointer ${
                      row.engagementRate < 75 ? 'bg-danger/3' : ''
                    }`}
                    onClick={() => handleChannelClick(row)}
                  >
                    <td className="px-4 py-3 font-medium text-text">{row.channel}</td>
                    <td className="px-4 py-3 text-right font-mono text-text">{formatNumber(row.sessions)}</td>
                    {compareEnabled && (
                      <td className="px-4 py-3 text-right">
                        {CHANNEL_COMPARISON[row.channel] && (
                          <DeltaIndicator current={row.sessions} yoyPrior={CHANNEL_COMPARISON[row.channel].yoy} popPrior={CHANNEL_COMPARISON[row.channel].pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right font-mono text-text-2">{formatNumber(row.engagedSessions)}</td>
                    <td className="px-4 py-3 text-right"><EngagementDot rate={row.engagementRate} /></td>
                    <td className="px-4 py-3 text-right font-mono text-text-2">{formatDuration(row.avgTime)}</td>
                    <td className="px-4 py-3 text-right font-mono text-text-2">{row.eventsPerSession.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-text">{row.keyEvents}</td>
                    <td className="px-4 py-3 text-right font-mono text-text-2">{formatPercentRaw(row.keyEventRate, 2)}</td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-text-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="bg-bg-2 font-semibold">
                  <td className="px-4 py-3 text-text">Total</td>
                  <td className="px-4 py-3 text-right font-mono text-text">{formatNumber(kpis.sessions)}</td>
                  {compareEnabled && (
                    <td className="px-4 py-3 text-right">
                      <DeltaIndicator current={kpis.sessions} yoyPrior={KPI_COMPARISON.sessions.yoy} popPrior={KPI_COMPARISON.sessions.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} />
                    </td>
                  )}
                  <td className="px-4 py-3 text-right font-mono text-text-2">{formatNumber(69_649)}</td>
                  <td className="px-4 py-3 text-right"><EngagementDot rate={kpis.engagementRate} /></td>
                  <td className="px-4 py-3 text-right font-mono text-text-2">{formatDuration(kpis.avgEngagementTime)}</td>
                  <td className="px-4 py-3 text-right font-mono text-text-2">6.36</td>
                  <td className="px-4 py-3 text-right font-mono text-text">{kpis.keyEvents}</td>
                  <td className="px-4 py-3 text-right font-mono text-text-2">{formatPercentRaw(0.08, 2)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 7. Geographic + Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm font-normal text-text-2 mb-4">Top Countries</h3>
          <div className="space-y-3">
            {[
              { country: 'United States', users: 70_000, pct: 98.6 },
              { country: 'Mexico', users: 218, pct: 0.31 },
              { country: 'Canada', users: 118, pct: 0.17 },
              { country: 'United Kingdom', users: 61, pct: 0.09 },
              { country: 'India', users: 55, pct: 0.08 },
              { country: 'Germany', users: 49, pct: 0.07 },
            ].map((c) => (
              <div key={c.country} className="flex items-center justify-between">
                <span className="text-xs text-text-2">{c.country}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-text">{formatNumber(c.users)}</span>
                  <span className="text-xs font-mono text-text-3 w-12 text-right">{c.pct}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm font-normal text-text-2 mb-4">User Activity</h3>
          <div className="space-y-4">
            {[
              { label: '30-day active', value: '75K' },
              { label: '7-day active', value: '17K' },
              { label: '1-day active', value: '2.3K' },
              { label: 'Realtime now', value: '41' },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <span className="text-xs text-text-2">{item.label}</span>
                <span className="text-sm font-mono font-bold text-text">{item.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-border">
            <h4 className="text-xs text-text-3 font-semibold uppercase tracking-wider mb-3">Top Events</h4>
            <div className="space-y-2">
              {[
                { event: 'page_view', count: '266K' },
                { event: 'session_start', count: '89K' },
                { event: 'first_visit', count: '71K' },
                { event: 'user_engagement', count: '63K' },
                { event: 'click', count: '23K' },
                { event: 'scroll', count: '20K' },
              ].map((e) => (
                <div key={e.event} className="flex items-center justify-between">
                  <span className="text-xs font-mono text-text-2">{e.event}</span>
                  <span className="text-xs font-mono text-text">{e.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm font-normal text-text-2 mb-4">Executive Summary</h3>
          <div className="space-y-4 text-xs text-text-3 leading-relaxed">
            <div>
              <p className="text-text-2 font-semibold mb-1">The Good</p>
              <p>Strong overall traffic (87.6K sessions). Organic Search delivers the highest quality visitors (91.7% engagement, 44s avg). Paid Search converts best (0.14% key event rate). Dining content is a compelling discovery driver.</p>
            </div>
            <div>
              <p className="text-danger font-semibold mb-1">The Problem</p>
              <p>Paid Social spends the most but delivers the worst engagement (60.5%, 3s avg). The booking funnel has a 99.1% drop between reservation clicks and form submissions. Retention is almost non-existent (3.4% return after 1 week).</p>
            </div>
            <div>
              <p className="text-monarch-gold font-semibold mb-1">Recommendations</p>
              <p>1) Audit the booking widget UX. 2) Reallocate Paid Social budget to Paid Search. 3) Build retargeting campaigns targeting the 13K who clicked reserve. 4) Invest in email capture for dining visitors.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Channel Drill Panel */}
      <ChannelDrillPanel channel={selectedChannel} open={drillOpen} onClose={handleDrillClose} />
    </motion.div>
  );
};

export default Dashboard;
